import { useEffect, useState } from 'react';
import { Layout, Table, Button, Modal, Form, Input, Select, message, Popconfirm, Tag, Space, Typography } from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../firebase';
import apis from '../../apis';
import { competitionList } from '../../constant/RegisterPageConst';

const { Content } = Layout;
const { Option } = Select;
const { Text } = Typography;

const JuryManagement = () => {
    const [juries, setJuries] = useState([]);
    const [loading, setLoading] = useState(false);
    
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [selectedJury, setSelectedJury] = useState(null);

    const [formCreate] = Form.useForm();
    const [formEdit] = Form.useForm();

    const fetchJuries = async () => {
        setLoading(true);
        try {
            const juriesQuery = query(
                collection(db, 'users'),
                where('role', '==', 'jury')
            );
            const juriesSnap = await getDocs(juriesQuery);
            const juriesData = juriesSnap.docs.map(d => ({ id: d.id, ...d.data() }));
            setJuries(juriesData);
        } catch (error) {
            console.error('Error fetching juries:', error);
            message.error('Failed to load juries');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchJuries();
    }, []);

    const handleCreateJurySubmit = async (values) => {
        setIsSubmitting(true);
        try {
            const response = await apis.jury.createJury(values);
            if (response && response.error) {
                const errorMsg = response.error?.data?.message || response.error?.data?.error || "Failed to create jury.";
                message.error(errorMsg);
                return;
            }
            message.success(`Jury member ${values.name} created successfully and email sent!`);
            setIsCreateModalOpen(false);
            formCreate.resetFields();
            fetchJuries();
        } catch (error) {
            console.error("Failed to create jury", error);
            const errorMsg = error.response?.data?.message || error.response?.data?.error || "Failed to create jury.";
            message.error(errorMsg);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleEditJurySubmit = async (values) => {
        setIsSubmitting(true);
        try {
            const payload = {
                uid: selectedJury.id,
                name: values.name,
                category: values.category,
                eventId: values.eventId
            };
            const response = await apis.jury.updateJury(payload.uid, payload);
            if (response && response.error) {
                const errorMsg = response.error?.data?.message || response.error?.data?.error || "Failed to update jury.";
                message.error(errorMsg);
                return;
            }
            message.success(`Jury member ${values.name} updated successfully!`);
            setIsEditModalOpen(false);
            formEdit.resetFields();
            fetchJuries();
        } catch (error) {
            console.error("Failed to update jury", error);
            const errorMsg = error.response?.data?.message || error.response?.data?.error || "Failed to update jury.";
            message.error(errorMsg);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDeleteJury = async (uid) => {
        try {
            const response = await apis.jury.deleteJury(uid);
            if (response && response.error) {
                const errorMsg = response.error?.data?.message || response.error?.data?.error || "Failed to delete jury.";
                message.error(errorMsg);
                return;
            }
            message.success(`Jury member deleted successfully!`);
            fetchJuries();
        } catch (error) {
            console.error("Failed to delete jury", error);
            const errorMsg = error.response?.data?.message || error.response?.data?.error || "Failed to delete jury.";
            message.error(errorMsg);
        }
    };

    const openEditModal = (jury) => {
        setSelectedJury(jury);
        formEdit.setFieldsValue({
            name: jury.name,
            category: jury.competitionCategory,
            eventId: jury.eventId
        });
        setIsEditModalOpen(true);
    };

    const columns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
        },
        {
            title: 'Category',
            dataIndex: 'competitionCategory',
            key: 'category',
            render: (text) => <Tag color="blue">{text}</Tag>
        },
        {
            title: 'Event ID',
            dataIndex: 'eventId',
            key: 'eventId',
            render: (text) => text ? <Tag color="purple">{text}</Tag> : <Text type="secondary">Not assigned</Text>
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space size="middle">
                    <Button type="primary" icon={<EditOutlined />} onClick={() => openEditModal(record)}>
                        Edit
                    </Button>
                    <Popconfirm
                        title="Delete Jury"
                        description="Are you sure you want to delete this jury member? This will fail if they have scored any students."
                        onConfirm={() => handleDeleteJury(record.id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button danger icon={<DeleteOutlined />}>
                            Delete
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        }
    ];

    return (
        <Content style={{ margin: '16px 16px' }}>
            <div style={{ padding: 24, background: '#fff', borderRadius: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                    <h2>Jury Management</h2>
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => setIsCreateModalOpen(true)}>
                        Create New Jury
                    </Button>
                </div>
                
                <Table 
                    columns={columns} 
                    dataSource={juries} 
                    rowKey="id" 
                    loading={loading}
                    pagination={{ pageSize: 10 }}
                />
            </div>

            {/* Create Jury Modal */}
            <Modal
                title="Create New Jury Member"
                open={isCreateModalOpen}
                onCancel={() => setIsCreateModalOpen(false)}
                footer={null}
            >
                <Form
                    form={formCreate}
                    layout="vertical"
                    onFinish={handleCreateJurySubmit}
                >
                    <Form.Item
                        name="name"
                        label="Full Name"
                        rules={[{ required: true, message: 'Please enter the name' }]}
                    >
                        <Input placeholder="John Doe" />
                    </Form.Item>

                    <Form.Item
                        name="email"
                        label="Email Address"
                        rules={[
                            { required: true, message: 'Please enter the email' },
                            { type: 'email', message: 'Please enter a valid email' }
                        ]}
                    >
                        <Input placeholder="jury@example.com" />
                    </Form.Item>

                    <Form.Item
                        name="password"
                        label="Password"
                        rules={[{ required: true, message: 'Please enter a password (min 6 chars)' }]}
                    >
                        <Input.Password placeholder="******" />
                    </Form.Item>

                    <Form.Item
                        name="category"
                        label="Competition Category"
                        rules={[{ required: true, message: 'Please select a category' }]}
                    >
                        <Select placeholder="Select category">
                            {Object.keys(competitionList).map((data) => (
                                <Option key={data} value={data}>{data}</Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="eventId"
                        label="Event ID"
                        rules={[{ required: true, message: 'Please enter Event ID (e.g. APCS2025)' }]}
                    >
                        <Input placeholder="e.g. APCS2025" />
                    </Form.Item>

                    <Form.Item>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                            <Button onClick={() => setIsCreateModalOpen(false)}>Cancel</Button>
                            <Button type="primary" htmlType="submit" loading={isSubmitting}>
                                Create Jury
                            </Button>
                        </div>
                    </Form.Item>
                </Form>
            </Modal>

            {/* Edit Jury Modal */}
            <Modal
                title="Edit Jury Member"
                open={isEditModalOpen}
                onCancel={() => setIsEditModalOpen(false)}
                footer={null}
            >
                <Form
                    form={formEdit}
                    layout="vertical"
                    onFinish={handleEditJurySubmit}
                >
                    <Form.Item
                        name="name"
                        label="Full Name"
                        rules={[{ required: true, message: 'Please enter the name' }]}
                    >
                        <Input placeholder="John Doe" />
                    </Form.Item>

                    <Form.Item
                        name="category"
                        label="Competition Category"
                        rules={[{ required: true, message: 'Please select a category' }]}
                    >
                        <Select placeholder="Select category">
                            {Object.keys(competitionList).map((data) => (
                                <Option key={data} value={data}>{data}</Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="eventId"
                        label="Event ID"
                        rules={[{ required: true, message: 'Please enter Event ID' }]}
                    >
                        <Input placeholder="e.g. APCS2025" />
                    </Form.Item>

                    <Form.Item>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                            <Button onClick={() => setIsEditModalOpen(false)}>Cancel</Button>
                            <Button type="primary" htmlType="submit" loading={isSubmitting}>
                                Save Changes
                            </Button>
                        </div>
                    </Form.Item>
                </Form>
            </Modal>
        </Content>
    );
};

export default JuryManagement;
